/**
 * FeedTimeline Component
 * 
 * Main timeline component with infinite scrolling and category filtering
 * Responsive: Sidebar for desktop, top section for mobile
 */

import React, { useEffect, useState } from 'react';
import {
    Box,
    VStack,
    HStack,
    Spinner,
    Text,
    Center,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Container,
    useBreakpointValue,
    Flex,
} from '@chakra-ui/react';
import { useInView } from 'react-intersection-observer';
import { useFeed } from './useFeed';
import { IconButton } from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import { FeedCard } from './FeedCard';
import { FeedFilter } from './FeedFilter';

export const FeedTimeline: React.FC = () => {
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>();
    const [selectedJobLevelId, setSelectedJobLevelId] = useState<number | undefined>();
    const [selectedAuthorName, setSelectedAuthorName] = useState<string | undefined>();
    
    const {
        feedItems,
        error,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
    } = useFeed({ 
        categoryId: selectedCategoryId,
        jobLevelId: selectedJobLevelId,
        authorName: selectedAuthorName,
    });

    const [showScrollTop, setShowScrollTop] = useState(false);

    // Determine if mobile view
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Intersection observer for infinite scroll
    const { ref, inView } = useInView({
        threshold: 0,
        rootMargin: '100px',
    });

    // Fetch next page when sentinel comes into view
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Scroll handler with throttling to prevent excessive updates
    useEffect(() => {
        let scrollTimeout: NodeJS.Timeout;
        
        const handleScroll = () => {
            // Clear previous timeout
            clearTimeout(scrollTimeout);
            
            // Debounce scroll updates
            scrollTimeout = setTimeout(() => {
                const scrollY = window.scrollY;
                
                // Update scroll button visibility
                const shouldShow = scrollY > 200;
                setShowScrollTop(shouldShow);
            }, 50); // 50ms debounce
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, []);

    const handleScrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleApplyFilters = (filters: {
        categoryId?: number;
        jobLevelId?: number;
        authorName?: string;
    }) => {
        setSelectedCategoryId(filters.categoryId);
        setSelectedJobLevelId(filters.jobLevelId);
        setSelectedAuthorName(filters.authorName);
    };

    // Loading state
    if (status === 'pending') {
        return (
            <Center h="400px">
                <VStack spacing={4}>
                    <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="customGreen.500"
                        size="xl"
                    />
                    <Text color="gray.600">Memuat feed...</Text>
                </VStack>
            </Center>
        );
    }

    // Error state
    if (status === 'error') {
        return (
            <Container maxW="full" py={8} px={isMobile ? 4 : 8}>
                <Alert
                    status="error"
                    variant="subtle"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    borderRadius={isMobile ? "0" : "lg"}
                    py={8}
                >
                    <AlertIcon boxSize="40px" mr={0} />
                    <AlertTitle mt={4} mb={1} fontSize="lg">
                        Gagal Memuat Feed
                    </AlertTitle>
                    <AlertDescription maxWidth="sm">
                        {error instanceof Error
                            ? error.message
                            : 'Terjadi kesalahan saat memuat data. Silakan coba lagi nanti.'}
                    </AlertDescription>
                </Alert>
            </Container>
        );
    }

    // Empty state
    if (feedItems.length === 0) {
        return (
            <Box>
                {/* Mobile Filter */}
                {isMobile && (
                    <Box p={4} borderBottomWidth="1px" borderBottomColor="gray.200">
                        <FeedFilter
                            selectedCategoryId={selectedCategoryId}
                            selectedJobLevelId={selectedJobLevelId}
                            selectedAuthorName={selectedAuthorName}
                            onApplyFilters={handleApplyFilters}
                            isMobile={true}
                        />
                    </Box>
                )}

                {/* Desktop Layout */}
                <Container maxW="full" py={isMobile ? 0 : 6} px={isMobile ? 0 : 14}>
                    <Flex gap={16} justifyContent="space-between" alignItems="flex-start">
                        {/* Desktop Sidebar */}
                        {!isMobile && (
                            <Box flexShrink={0}>
                                <Box position="sticky" top="80px">
                                    <FeedFilter
                                        selectedCategoryId={selectedCategoryId}
                                        selectedJobLevelId={selectedJobLevelId}
                                        selectedAuthorName={selectedAuthorName}
                                        onApplyFilters={handleApplyFilters}
                                        isMobile={false}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Main Content */}
                        <Box flex={1} maxW={isMobile ? "100%" : "container.md"}>
                            <Center h="300px">
                                <VStack spacing={3}>
                                    <Text fontSize="4xl">📋</Text>
                                    <Text fontSize="lg" fontWeight="medium" color="gray.600">
                                        Belum ada inspeksi
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                        Feed akan muncul ketika ada laporan inspeksi baru
                                    </Text>
                                </VStack>
                            </Center>
                        </Box>

                        {/* Invisible Box to Balance Layout */}
                        {!isMobile && (
                            <Box flexShrink={0} w="250px" />
                        )}
                    </Flex>
                </Container>
            </Box>
        );
    }

    return (
        <Box>
            {/* Mobile Filter */}
            {isMobile && (
                <Box p={4} borderBottomWidth="1px" borderBottomColor="gray.200">
                    <FeedFilter
                        selectedCategoryId={selectedCategoryId}
                        selectedJobLevelId={selectedJobLevelId}
                        selectedAuthorName={selectedAuthorName}
                        onApplyFilters={handleApplyFilters}
                        isMobile={true}
                    />
                </Box>
            )}

            {/* Desktop Layout */}
            <Container maxW="full" py={isMobile ? 0 : 6} px={isMobile ? 0 : 14}>
                <Flex gap={16} justify="center" alignItems="flex-start">
                    {/* Desktop Sidebar */}
                    {!isMobile && (
                        <Box flexShrink={0}>
                            <Box position="sticky" top="80px">
                                <FeedFilter
                                    selectedCategoryId={selectedCategoryId}
                                    selectedJobLevelId={selectedJobLevelId}
                                    selectedAuthorName={selectedAuthorName}
                                    onApplyFilters={handleApplyFilters}
                                    isMobile={false}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Main Content */}
                    <Box flex={1} maxW={isMobile ? "100%" : "container.md"}>
                        <VStack spacing={isMobile ? 0 : 6} align="stretch">
                            {/* Feed Items */}
                            {feedItems.map((item) => (
                                <FeedCard key={item.id} item={item} />
                            ))}

                            {/* Intersection Observer Sentinel */}
                            <Box ref={ref} py={4}>
                                {isFetchingNextPage && (
                                    <Center>
                                        <VStack spacing={2}>
                                            <Spinner
                                                thickness="3px"
                                                speed="0.65s"
                                                emptyColor="gray.200"
                                                color="customGreen.500"
                                                size="md"
                                            />
                                            <Text fontSize="sm" color="gray.500">
                                                Memuat lebih banyak...
                                            </Text>
                                        </VStack>
                                    </Center>
                                )}

                                {!hasNextPage && !isFetching && feedItems.length > 0 && (
                                    <Center>
                                        <Text fontSize="sm" color="gray.500" py={4}>
                                            Semua data telah dimuat
                                        </Text>
                                    </Center>
                                )}
                            </Box>
                        </VStack>
                    </Box>

                    {/* Invisible Box to Balance Layout */}
                    {!isMobile && (
                        <Box flexShrink={0} w="250px" />
                    )}
                </Flex>
            </Container>

            {/* Scroll to Top Button - Positioned outside Container */}
            {showScrollTop && (
                    <IconButton
                        aria-label="Scroll ke atas"
                        icon={<ArrowUpIcon boxSize={6} />}
                        position="fixed"
                        bottom={8}
                        right={8}
                        zIndex={1005}
                        bg="customGreen.500"
                        color="white"
                        size="sm"
                        isRound
                        shadow="sm"
                        onClick={handleScrollToTop}
                        _hover={{ 
                            bg: "customGreen.600",
                            transform: "scale(1.05)"
                        }}
                        _active={{
                            bg: "customGreen.700",
                            transform: "scale(0.95)"
                        }}
                        border="2px solid white"
                        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                    />
                )}
        </Box>
    );
};